import { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { regenerateAmountScalers } from '../utils/amountRandomizer';

type DisplayPrivacyContextValue = {
  randomizeAmounts: boolean;
  setRandomizeAmounts: (enabled: boolean) => void;
  toggleRandomizeAmounts: () => void;
};

const DisplayPrivacyContext = createContext<DisplayPrivacyContextValue | undefined>(undefined);

const STORAGE_KEY = 'autofi-randomize-amounts';

const getStoredRandomizeAmounts = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  return window.localStorage.getItem(STORAGE_KEY) === 'true';
};

export const DisplayPrivacyProvider = ({ children }: { children: ReactNode }) => {
  const [randomizeAmounts, setRandomizeAmountsState] = useState<boolean>(() => {
    const storedValue = getStoredRandomizeAmounts();
    if (storedValue) {
      regenerateAmountScalers();
    }
    return storedValue;
  });

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    window.localStorage.setItem(STORAGE_KEY, String(randomizeAmounts));
  }, [randomizeAmounts]);

  const setRandomizeAmounts = (enabled: boolean) => {
    setRandomizeAmountsState((previousState) => {
      if (!previousState && enabled) {
        regenerateAmountScalers();
      }
      return enabled;
    });
  };

  const toggleRandomizeAmounts = () => {
    setRandomizeAmountsState((previousState) => {
      const nextState = !previousState;
      if (!previousState && nextState) {
        regenerateAmountScalers();
      }
      return nextState;
    });
  };

  return (
    <DisplayPrivacyContext.Provider value={{ randomizeAmounts, setRandomizeAmounts, toggleRandomizeAmounts }}>
      {children}
    </DisplayPrivacyContext.Provider>
  );
};

export const useDisplayPrivacy = (): DisplayPrivacyContextValue => {
  const context = useContext(DisplayPrivacyContext);
  if (!context) {
    throw new Error('useDisplayPrivacy must be used within a DisplayPrivacyProvider');
  }

  return context;
};
